import { supabase } from "@/lib/supabase/browser-client"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { buildSpaceTaskNotificationSnapshot } from "@/lib/notifications/space-task-events"
import {
  canDispatchSpaceTaskNotificationEvents,
  dispatchSpaceTaskNotificationEvents
} from "@/lib/notifications/space-task-notification-client"
import { refreshSpaceSidebar } from "@/lib/spaces/sidebar-refresh"
import { Database } from "@/supabase/types"
import {
  SpaceSprint,
  SpaceSprintInsert,
  SpaceSprintUpdate,
  SprintStatus
} from "@/types/spaces"
import { SupabaseClient } from "@supabase/supabase-js"

const parseSprintDate = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number)
  return new Date(year, month - 1, day)
}

const formatSprintDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const addSprintDays = (date: Date, days: number) => {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + days)
  return nextDate
}

const getAutoSprintStatus = (
  startDate: string,
  endDate: string,
  currentStatus?: string | null
): SprintStatus => {
  if (currentStatus === "cancelled" || currentStatus === "completed") {
    return currentStatus
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const sprintStart = parseSprintDate(startDate)
  sprintStart.setHours(0, 0, 0, 0)

  const sprintEnd = parseSprintDate(endDate)
  sprintEnd.setHours(0, 0, 0, 0)

  if (today < sprintStart) {
    return "planned"
  }

  if (today > sprintEnd) {
    return "completed"
  }

  return "active"
}

const withResolvedSprintStatus = <
  T extends { start_date: string; end_date: string; status: string | null }
>(
  sprint: T
) => ({
  ...sprint,
  status: getAutoSprintStatus(sprint.start_date, sprint.end_date, sprint.status)
})

const withResolvedMainSprint = <
  T extends {
    start_date: string
    end_date: string
    status: string | null
    sub_sprints?: Array<{
      start_date: string
      end_date: string
      status: string | null
    }>
  }
>(
  sprint: T
) => ({
  ...withResolvedSprintStatus(sprint),
  sub_sprints: sprint.sub_sprints?.map(subSprint =>
    withResolvedSprintStatus(subSprint)
  )
})

const withResolvedDetailedSprint = <
  T extends {
    start_date: string
    end_date: string
    status: string | null
    sub_sprints?: Array<{
      start_date: string
      end_date: string
      status: string | null
    }>
    parent_sprint?: {
      start_date: string
      end_date: string
      status: string | null
    } | null
  }
>(
  sprint: T
) => ({
  ...withResolvedMainSprint(sprint),
  parent_sprint: sprint.parent_sprint
    ? withResolvedSprintStatus(sprint.parent_sprint)
    : sprint.parent_sprint
})

const buildSubSprintInserts = (mainSprint: SpaceSprint): SpaceSprintInsert[] => {
  if (!mainSprint.is_main_sprint || !mainSprint.sprint_duration) {
    return []
  }

  const durationDays = Math.max(1, mainSprint.sprint_duration)
  const sprintEnd = parseSprintDate(mainSprint.end_date)
  let currentStart = parseSprintDate(mainSprint.start_date)
  let sprintNumber = 1
  const subSprints: SpaceSprintInsert[] = []

  while (currentStart <= sprintEnd) {
    const currentEnd = addSprintDays(currentStart, durationDays - 1)
    const boundedEnd = currentEnd <= sprintEnd ? currentEnd : sprintEnd
    const startDate = formatSprintDate(currentStart)
    const endDate = formatSprintDate(boundedEnd)

    subSprints.push({
      user_id: mainSprint.user_id,
      space_id: mainSprint.space_id,
      name: `Sprint ${sprintNumber}`,
      description: mainSprint.description,
      goal: mainSprint.goal,
      start_date: startDate,
      end_date: endDate,
      status: getAutoSprintStatus(startDate, endDate, "planned"),
      parent_sprint_id: mainSprint.id,
      sprint_number: sprintNumber,
      is_main_sprint: false
    })

    currentStart = addSprintDays(boundedEnd, 1)
    sprintNumber += 1
  }

  return subSprints
}

const readSpaceRouteErrorMessage = async (
  response: Response,
  fallback: string
) => {
  const responseText = await response.text()

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

export const getSpaceSprints = async (spaceId: string, includeSubSprints = false) => {
  let query = supabase
    .from("space_sprints")
    .select("*")
    .eq("space_id", spaceId)
  if (!includeSubSprints) {
    query = query.eq("is_main_sprint", true)
  }
  const { data, error } = await query.order("start_date", { ascending: false })

  if (error) throw error
  return data.map(sprint => withResolvedSprintStatus(sprint))
}

export const getSprintById = async (sprintId: string) => {
  const { data, error } = await supabase
    .from("space_sprints")
    .select(
      `
      *,
      tasks:space_tasks(*),
      sub_sprints:space_sprints!parent_sprint_id(*),
      parent_sprint:space_sprints!parent_sprint_id(*)
    `
    )
    .eq("id", sprintId)
    .single()

  if (error) throw error
  return withResolvedDetailedSprint(data)
}

export const getActiveSprint = async (spaceId: string) => {
  const { data, error } = await supabase
    .from("space_sprints")
    .select("*")
    .eq("space_id", spaceId)
    .eq("status", "active")
    .eq("is_main_sprint", false)
    .order("start_date", { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error

  return data ? withResolvedSprintStatus(data) : data
}

export const createSprint = async (
  sprint: SpaceSprintInsert,
  supabaseClient?: SupabaseClient<Database>
): Promise<SpaceSprint> => {
  if (!supabaseClient && typeof window !== "undefined") {
    const response = await authenticatedFetch(`/api/spaces/${sprint.space_id}/sprints`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(sprint)
    })

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(response, "Failed to create sprint")
      )
    }

    return (await response.json()) as SpaceSprint
  }

  const client = supabaseClient || supabase
  const sprintToCreate: SpaceSprintInsert = {
    ...sprint,
    status: getAutoSprintStatus(sprint.start_date, sprint.end_date, sprint.status)
  }

  const { data, error } = await client
    .from("space_sprints")
    .insert(sprintToCreate)
    .select()
    .single()

  if (error) throw error

  const subSprints = buildSubSprintInserts(data)
  if (subSprints.length > 0) {
    const { error: subSprintError } = await client
      .from("space_sprints")
      .insert(subSprints)

    if (subSprintError) {
      await client.from("space_sprints").delete().eq("id", data.id)
      throw subSprintError
    }
  }

  if (data?.space_id) {
    refreshSpaceSidebar(data.space_id)
  }

  return data
}

export const updateSprint = async (
  sprintId: string,
  updates: SpaceSprintUpdate
) => {
  const { data, error } = await supabase
    .from("space_sprints")
    .update(updates)
    .eq("id", sprintId)
    .select()
    .single()

  if (error) throw error

  const shouldRefreshSidebar =
    "name" in updates ||
    "space_id" in updates ||
    "archived_at" in updates

  if (shouldRefreshSidebar && data?.space_id) {
    refreshSpaceSidebar(data.space_id)
  }

  return data
}

export const deleteSprint = async (sprintId: string) => {
  const archivedAt = new Date().toISOString()
  const { data, error } = await supabase
    .from("space_sprints")
    .update({
      archived_at: archivedAt,
      updated_at: archivedAt
    })
    .eq("id", sprintId)
    .is("archived_at", null)
    .select("space_id")
    .maybeSingle()

  if (error) throw error

  if (data?.space_id) {
    refreshSpaceSidebar(data.space_id)
  }
}

export const updateSprintStatus = async (
  sprintId: string,
  status: SprintStatus
) => {
  return updateSprint(sprintId, { status })
}

export const startSprint = async (sprintId: string) => {
  return updateSprint(sprintId, { status: "active" })
}

export const completeSprint = async (sprintId: string) => {
  return updateSprint(sprintId, { status: "completed" })
}

export const getSprintStats = async (sprintId: string) => {
  const { data: tasks, error } = await supabase
    .from("space_tasks")
    .select("status, estimated_hours, actual_hours")
    .eq("sprint_id", sprintId)

  if (error) throw error

  const stats = {
    total_tasks: tasks.length,
    completed_tasks: tasks.filter(t => t.status === "done").length,
    total_estimated_hours: tasks.reduce(
      (sum, t) => sum + (t.estimated_hours || 0),
      0
    ),
    total_actual_hours: tasks.reduce((sum, t) => sum + (t.actual_hours || 0), 0),
    completion_rate: tasks.length
      ? (tasks.filter(t => t.status === "done").length / tasks.length) * 100
      : 0
  }

  return stats
}

export const getMainSprintsWithSubSprints = async (spaceId: string, includeArchived?: boolean) => {
  let query = supabase
    .from("space_sprints")
    .select(
      `
      *,
      sub_sprints:space_sprints!parent_sprint_id(
        *,
        tasks:space_tasks(*)
      )
    `
    )
    .eq("space_id", spaceId)
    .eq("is_main_sprint", true)
  
  // Exclude archived sprints by default unless includeArchived is true
  if (!includeArchived) {
    query = query.is("archived_at", null)
  }
  
  query = query.order("start_date", { ascending: false })
  
  const { data, error } = await query

  if (error) throw error
  return data.map(sprint => withResolvedMainSprint(sprint))
}

export const getSubSprintsForMain = async (mainSprintId: string, includeArchived?: boolean) => {
  let query = supabase
    .from("space_sprints")
    .select(
      `
      *,
      tasks:space_tasks(*)
    `
    )
    .eq("parent_sprint_id", mainSprintId)
  
  // Exclude archived sprints by default unless includeArchived is true
  if (!includeArchived) {
    query = query.is("archived_at", null)
  }
  
  query = query.order("sprint_number", { ascending: true })
  
  const { data, error } = await query

  if (error) throw error
  return data.map(sprint => withResolvedSprintStatus(sprint))
}

export const getCurrentActiveSprint = async (spaceId: string) => {
  const now = new Date().toISOString()
  const { data, error } = await supabase
    .from("space_sprints")
    .select("*")
    .eq("space_id", spaceId)
    .eq("is_main_sprint", false)
    .lte("start_date", now)
    .gte("end_date", now)
    .order("start_date", { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error

  return data ? withResolvedSprintStatus(data) : data
}

export const duplicateSprint = async (
  sprintId: string,
  newName?: string
) => {
  // Get the original sprint with all sub-sprints and tasks
  const { data: originalSprint, error: fetchError } = await supabase
    .from("space_sprints")
    .select(`
      *,
      sub_sprints:space_sprints!parent_sprint_id(*),
      tasks:space_tasks(*)
    `)
    .eq("id", sprintId)
    .single()

  if (fetchError) throw fetchError

  // Create the duplicate sprint
  const duplicateSprintData = {
    ...originalSprint,
    id: undefined,
    name: newName || `${originalSprint.name} (Copy)`,
    status: "planned", // Reset status to planned
    created_at: undefined,
    updated_at: undefined
  }

  const { data: newSprint, error: createError } = await supabase
    .from("space_sprints")
    .insert(duplicateSprintData)
    .select()
    .single()

  if (createError) throw createError

  // If this is a main sprint, duplicate sub-sprints and their tasks
  if (originalSprint.is_main_sprint && originalSprint.sub_sprints?.length > 0) {
    for (const subSprint of originalSprint.sub_sprints) {
      const subSprintDuplicateData = {
        ...subSprint,
        id: undefined,
        parent_sprint_id: newSprint.id,
        space_id: newSprint.space_id,
        user_id: newSprint.user_id,
        status: "planned", // Reset status to planned
        created_at: undefined,
        updated_at: undefined
      }

      const { data: newSubSprint, error: subCreateError } = await supabase
        .from("space_sprints")
        .insert(subSprintDuplicateData)
        .select()
        .single()

      if (subCreateError) throw subCreateError

      // Duplicate tasks for this sub-sprint
      const { data: subSprintTasks } = await supabase
        .from("space_tasks")
        .select("*")
        .eq("sprint_id", subSprint.id)

      if (subSprintTasks?.length > 0) {
        const taskCopies = subSprintTasks.map(task => ({
          ...task,
          id: undefined,
          sprint_id: newSubSprint.id,
          created_at: undefined,
          updated_at: undefined
        }))

        const { data: duplicatedTasks, error: taskError } = await supabase
          .from("space_tasks")
          .insert(taskCopies)
          .select()

        if (taskError) throw taskError

        if (duplicatedTasks && canDispatchSpaceTaskNotificationEvents()) {
          void dispatchSpaceTaskNotificationEvents(
            duplicatedTasks.map(task => ({
              after: buildSpaceTaskNotificationSnapshot(task)
            }))
          )
        }
      }
    }
  }

  // If this is a sub-sprint, duplicate its tasks
  if (!originalSprint.is_main_sprint && originalSprint.tasks?.length > 0) {
    const taskCopies = originalSprint.tasks.map(task => ({
      ...task,
      id: undefined,
      created_at: undefined,
      updated_at: undefined
    }))

    const { data: duplicatedTasks, error: taskError } = await supabase
      .from("space_tasks")
      .insert(taskCopies)
      .select()

    if (taskError) throw taskError

    if (duplicatedTasks && canDispatchSpaceTaskNotificationEvents()) {
      void dispatchSpaceTaskNotificationEvents(
        duplicatedTasks.map(task => ({
          after: buildSpaceTaskNotificationSnapshot(task)
        }))
      )
    }
  }

  return newSprint
}
